import React, { useState } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, Platform } from 'react-native';
import { Controller } from 'react-hook-form';
import { useTheme } from '../../../theme/ThemeProvider';
import Colors from '../../../assets/Color/Colors';
import { globalFontStyle } from '../../../assets';
import './Input.web.css';
import FastImage from '../../../common/FastImage';
import {globalConfigurableData} from '../../../utils/globalConstant';

const InputConfig = {
  dimensions: {
    minWidth: 100,
    borderRadius: 8,
    iconSize: {
      sm: 16,
      default: 20,
      lg: 24,
    },
    height: {
      sm: Platform.select({ ios: 40, android: 38, default: 40 }),
      default: Platform.select({ ios: 48, android: 46, default: 48 }),
      lg: Platform.select({ ios: 56, android: 54, default: 56 }),
    },
    padding: {
      sm: Platform.select({ ios: 10, android: 8, default: 10 }),
      default: Platform.select({ ios: 12, android: 10, default: 12 }),
      lg: Platform.select({ ios: 14, android: 12, default: 14 }),
    }
  },
  typography: {
    fontSize: {
      sm: 14,
      default: 16,
      lg: 18,
    },
    labelSize: {
      sm: 12,
      default: 14,
      lg: 16,
    },
    errorSize: 12,
    lineHeight: Platform.OS === 'web' ? {
      web: {
        sm: '40px',
        default: '48px',
        lg: '56px'
      }
    } : undefined,
  },
  spacing: {
    marginBottom: 18, // Added margin bottom
    labelSpacing: 8,
    errorSpacing: 4,
    errorLeftSpacing: 4,
  },
  animation: {
    focusedBorderWidth: 1.5,
    defaultBorderWidth: 1,
    transitionDuration: '0.2s',
    shadowConfig: {
      color: '#000',
      offset: { width: 0, height: 0 },
      opacity: 0.1,
      radius: 3,
      elevation: 2,
    }
  },
  variants: {
    default: {
      background: 'background',
      border: 'border',
      text: 'text',
      placeholder: 'textTertiary',
    },
    filled: {
      background: 'backgroundSecondary',
      border: 'transparent',
      text: 'text',
      placeholder: 'textTertiary',
    },
    outline: {
      background: 'transparent',
      border: 'border',
      text: 'text',
      placeholder: 'textTertiary',
    },
    error: {
      background: 'errorLight',
      border: 'error',
      text: 'text',
      placeholder: 'textTertiary',
    }
  },
  addons: {
    button: {
      height: 32,
      paddingHorizontal: 12,
      borderRadius: 16,
      marginRight: 8,
    },
    icon: {
      size: {
        sm: 16,
        default: 20,
        lg: 24,
      },
      spacing: 8,
    },
    counter: {
      width: 35,
      height: 35,
    },
    prefix: {
      spacing: 8,
      fontSize: 15,
    }
  },
  formatters: {
    pan: (value) => {
      if (!value) return value;
      return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    },
    ifsc: (value) => {
      if (!value) return value;
      return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    },
    mobile: (value) => {
      if (!value) return value;
      return value.replace(/[^0-9]/g, '');
    }
  },
  badges: {
    dimensions: {
      height: 20,
      paddingHorizontal: 8,
      borderRadius: 10,
      marginLeft: 8,
    },
    typography: {
      fontSize: 12,
    },
    variants: {
      default: {
        background: 'backgroundSecondary',
        text: 'textSecondary',
      },
      success: {
        background: 'successLight',
        text: 'success',
      },
      error: {
        background: 'errorLight',
        text: 'error',
      },
      warning: {
        background: 'warningLight',
        text: 'warning',
      },
      info: {
        background: 'infoLight',
        text: 'info',
      }
    }
  },
};

/**
 * Modern Input Component with shadcn-inspired design
 * @param {Object} props
 * @param {Object} props.control - React Hook Form control object
 * @param {string} props.name - Field name
 * @param {Object} props.rules - Validation rules
 * @param {string} props.placeholder - Input placeholder
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {boolean} props.numeric - Whether the input should accept only numbers
 * @param {string} props.scale - Scale configuration ("0-100", "0-10", "0-1" or null)
 * @param {Function} props.onFocus - Focus callback
 * @param {Object} props.style - Additional style for the input container
 * @param {Object} props.containerStyle - Additional style for the outer container
 * @param {Object} props.inputStyle - Additional style for the input field
 * @param {string} props.label - Input label
 * @param {boolean} props.fullWidth - Whether the input should take full width
 * @param {boolean} props.textAlignRight - Whether to align text to the right
 * @param {boolean} props.selectTextOnFocus - Whether to select text on focus
 * @param {'sm' | 'default' | 'lg'} props.size - Input size variant
 * @param {'default' | 'filled' | 'outline' | 'error'} props.variant - Input style variant
 * @param {string} props.className - Additional CSS class name for web
 * @param {Function} props.leftIcon - Left icon component or source
 * @param {Function} props.rightIcon - Right icon component or source
 * @param {Object} props.iconStyle - Style for icons
 * @param {boolean} props.showIncrementButtons - Show increment/decrement buttons
 * @param {Function} props.onIncrement - Increment handler
 * @param {Function} props.onDecrement - Decrement handler
 * @param {string} props.prefix - Text prefix (like country code)
 * @param {Object} props.prefixStyle - Style for prefix
 * @param {Object} props.button - Button configuration { text, onPress, variant }
 * @param {string} props.formatter - Predefined formatter ('pan', 'ifsc', 'mobile')
 * @param {Object} props.badge - Badge configuration { text, variant, style, textStyle }
 * @param {boolean} props.hideError - Whether to hide the internal error message
 * @param {boolean} props.isDateInput - Whether the input is a date input
 * @param {string} props.backgroundColor - Custom background color for the input field
 */
export const Input = ({
  control,
  name,
  rules = {},
  placeholder = '',
  disabled = false,
  numeric = false,
  scale = null,
  onFocus,
  style,
  containerStyle,
  inputStyle,
  label,
  fullWidth = false,
  textAlignRight = false,
  selectTextOnFocus = false,
  size = 'default',
  variant = 'default',
  className = '',
  leftIcon,
  rightIcon,
  iconStyle,
  showIncrementButtons,
  onIncrement,
  onDecrement,
  prefix,
  prefixStyle,
  button,
  formatter,
  badge,
  hideError = false,
  isDateInput = false,
  backgroundColor,
  ...props
}) => {
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState(false);

  const formatValue = (value) => {
    if (formatter && InputConfig.formatters[formatter]) {
      return InputConfig.formatters[formatter](value);
    }
    return value;
  };

  const handleChangeText = (text, onChange) => {
    let processedText = text;
    
    if (formatter) {
      processedText = formatValue(text);
    }
    
    // Handle numeric input
    if (numeric) {
      processedText = processedText.replace(/[^0-9.]/g, '');
      
      if (processedText === '') {
        onChange('');
        return;
      }

      // Handle scale validation if scale is provided
      if (scale) {
        const [min, max] = scale.split('-').map(Number);
        const numericValue = Number(processedText);
        
        if (!isNaN(numericValue) && numericValue >= min && numericValue <= max) {
          onChange(processedText);
        }
        return;
      }

      onChange(processedText);
      return;
    }

    onChange(processedText);
  };

  const getVariantStyles = () => {
    const defaultColors = {
      background: Colors.bgWhite,
      border: Colors.borderColor,
      text: Colors.black,
      textTertiary: Colors.grey,
    };

    const variantConfig = InputConfig.variants[variant] || InputConfig.variants.default;
    const variantStyles = {
      background: variantConfig?.background || 'background',
      border: variantConfig?.border || 'border',
      text: variantConfig?.text || 'text',
      placeholder: variantConfig?.placeholder || 'textTertiary',
    };

    return {
      backgroundColor: theme?.colors?.[variantStyles.background] || defaultColors.background,
      borderColor: isFocused 
        ? (theme?.colors?.primary || Colors.primary) 
        : (theme?.colors?.[variantStyles.border] || defaultColors.border),
      color: theme?.colors?.[variantStyles.text] || defaultColors.text,
      placeholderColor: (theme?.colors?.textTertiary || defaultColors.textTertiary) + '99',
    };
  };

  const getSizeStyles = () => {
    const baseStyles = {
      height: InputConfig.dimensions.height[size],
      padding: InputConfig.dimensions.padding[size],
      fontSize: InputConfig.typography.fontSize[size],
      backgroundColor: 'transparent',
    };

    if (Platform.OS === 'web' && InputConfig.typography.lineHeight) {
      baseStyles.lineHeight = InputConfig.typography.lineHeight.web[size];
    }

    return baseStyles;
  };

  const getInputContainerStyle = () => {
    const variantConfig = InputConfig.variants[variant] || InputConfig.variants.default;
    return {
      backgroundColor: backgroundColor || theme[variantConfig.background],
      borderColor: theme[variantConfig.border],
      borderWidth: isFocused ? InputConfig.animation.focusedBorderWidth : InputConfig.animation.defaultBorderWidth,
      borderRadius: InputConfig.dimensions.borderRadius,
      minHeight: InputConfig.dimensions.height[size],
      padding: InputConfig.dimensions.padding[size],
      ...style,
    };
  };

  const styles = StyleSheet.create({
    container: {
      marginBottom: InputConfig.spacing.marginBottom,
      width: fullWidth ? '100%' : 'auto',
      minWidth: InputConfig.dimensions.minWidth,
      ...containerStyle,
    },
    inputContainer: {
      borderWidth: isFocused ? 
        InputConfig.animation.focusedBorderWidth : 
        InputConfig.animation.defaultBorderWidth,
      borderRadius: InputConfig.dimensions.borderRadius,
      overflow: 'hidden',
      ...getVariantStyles(),
      ...Platform.select({
        web: {
          transitionProperty: 'all',
          transitionDuration: InputConfig.animation.transitionDuration,
          transitionTimingFunction: 'ease-in-out',
          outlineStyle: 'none',
          outlineWidth: 0,
        },
        default: {},
      }),
      ...style,
    },
    input: {
      ...getSizeStyles(),
      fontFamily: globalFontStyle.appFontBody.fontFamily,
      textAlign: textAlignRight ? 'right' : 'left',
      color: getVariantStyles().color,
      flex: 1,
      ...Platform.select({
        web: {
          outlineStyle: 'none',
          outlineWidth: 0,
          borderWidth: 0,
          cursor: disabled ? 'not-allowed' : 'text',
          paddingLeft: prefix ? 45 : InputConfig.dimensions.padding[size],
        },
        default: {
          paddingLeft: prefix ? 45 : InputConfig.dimensions.padding[size],
        },
      }),
      ...inputStyle,
    },
    error: {
      color: theme.colors.error,
      fontSize: InputConfig.typography.errorSize,
      marginTop: InputConfig.spacing.errorSpacing,
      marginLeft: InputConfig.spacing.errorLeftSpacing,
      fontFamily: globalFontStyle.appFontBody.fontFamily,
    },
    label: {
      fontSize: InputConfig.typography.labelSize[size],
      color: theme?.colors?.textTertiary || Colors.grey,
      opacity: 0.6,
      marginBottom: InputConfig.spacing.labelSpacing,
      fontFamily: globalFontStyle.appFontBody.fontFamily,
    },
    focusedContainer: {
      ...Platform.select({
        ios: {
          shadowColor: InputConfig.animation.shadowConfig.color,
          shadowOffset: InputConfig.animation.shadowConfig.offset,
          shadowOpacity: InputConfig.animation.shadowConfig.opacity,
          shadowRadius: InputConfig.animation.shadowConfig.radius,
        },
        android: {
          elevation: InputConfig.animation.shadowConfig.elevation,
        },
        web: {
          boxShadow: `0 0 0 2px ${theme.colors.primaryLight}`,
        },
      }),
    },
    disabled: Platform.select({
      web: {
        opacity: 0.6,
        cursor: 'not-allowed',
      },
      default: {
        opacity: 0.6,
      },
    }),
    prefix: {
      flexDirection: 'row',
      alignItems: 'center',
      height: '100%',
      position: 'absolute',
      left: 12,
      top: 0,
      zIndex: 1,
    },
    prefixIcon: {
      width: 22,
      height: 22,
      marginRight: 8,
    },
    icon: {
      position: 'absolute',
      top: '50%',
      transform: [{ translateY: -InputConfig.addons.icon.size[size] / 2 }],
      zIndex: 1,
    },
    iconLeft: {
      left: InputConfig.addons.prefix.spacing,
    },
    iconRight: {
      right: InputConfig.addons.prefix.spacing,
    },
    iconImage: {
      width: InputConfig.addons.icon.size[size],
      height: InputConfig.addons.icon.size[size],
    },
    button: {
      height: InputConfig.addons.button.height,
      paddingHorizontal: InputConfig.addons.button.paddingHorizontal,
      borderRadius: InputConfig.addons.button.borderRadius,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: InputConfig.addons.button.marginRight,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    buttonPrimary: {
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    },
    buttonText: {
      color: theme.colors.text,
      ...globalFontStyle.appFontBody,
    },
    buttonTextPrimary: {
      color: theme.colors.background,
    },
    counterButton: {
      width: InputConfig.addons.counter.width,
      height: InputConfig.addons.counter.height,
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      top: '50%',
      transform: [{ translateY: -InputConfig.addons.counter.height / 2 }],
      left: InputConfig.addons.prefix.spacing,
    },
    counterButtonRight: {
      left: undefined,
      right: InputConfig.addons.prefix.spacing,
    },
    counterIcon: {
      width: InputConfig.addons.counter.width,
      height: InputConfig.addons.counter.height,
    },
    badge: {
      height: InputConfig.badges.dimensions.height,
      paddingHorizontal: InputConfig.badges.dimensions.paddingHorizontal,
      borderRadius: InputConfig.badges.dimensions.borderRadius,
      marginLeft: InputConfig.badges.dimensions.marginLeft,
      justifyContent: 'center',
      alignItems: 'center',
    },
    badgeText: {
      fontSize: InputConfig.badges.typography.fontSize,
      fontFamily: globalFontStyle.appFontBody.fontFamily,
    },
  });

  const renderPrefix = () => {
    if (!prefix) return null;
    if (typeof prefix === 'string' && !prefix.match(/\.(jpg|jpeg|png|gif|bmp|svg|webp|ico)$/i)) {
      return (
        <View style={[styles.prefix, prefixStyle, { alignItems: 'center', marginRight: 8 }]}>
          <Text style={styles.buttonText}>{prefix}</Text>
        </View>
      );
    }

    return (
      <View style={[styles.prefix, prefixStyle]}>
        <FastImage
          source={typeof prefix === 'string' ? {uri: prefix} : prefix}
          style={styles.prefixIcon}
          resizeMode="contain"
        />
      </View>
    );
  };

  const renderIcon = (icon, position) => {
    if (!icon) return null;

    return (
      <View style={[
        styles.icon,
        position === 'right' ? styles.iconRight : styles.iconLeft
      ]}>
        {typeof icon === 'function' ? (
          icon()
        ) : (
          <FastImage
            source={icon}
            style={[styles.iconImage, iconStyle]}
          />
        )}
      </View>
    );
  };

  const renderButton = () => {
    if (!button) return null;

    return (
      <TouchableOpacity
        style={[
          styles.button,
          button.variant === 'primary' && styles.buttonPrimary,
          button.style
        ]}
        onPress={button.onPress}
        disabled={button.disabled}
      >
        <Text style={[
          styles.buttonText,
          button.variant === 'primary' && styles.buttonTextPrimary,
          button.textStyle
        ]}>
          {button.text}
        </Text>
      </TouchableOpacity>
    );
  };

  const renderIncrementButtons = () => {
    if (!showIncrementButtons) return null;

    return (
      <>
        <TouchableOpacity
          style={styles.counterButton}
          onPress={onDecrement}
        >
          <FastImage
            style={styles.counterIcon}
            source={globalConfigurableData.ic_minus()}
          />
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.counterButton, styles.counterButtonRight]}
          onPress={onIncrement}
        >
          <FastImage
            style={styles.counterIcon}
            source={globalConfigurableData.ic_plus()}
          />
        </TouchableOpacity>
      </>
    );
  };

  const renderBadge = () => {
    if (!badge) return null;

    const badgeVariant = InputConfig.badges.variants[badge.variant || 'default'];
    
    return (
      <View style={[
        styles.badge,
        {
          backgroundColor: theme.colors[badgeVariant.background],
        },
        badge.style
      ]}>
        <Text style={[
          styles.badgeText,
          {
            color: theme.colors[badgeVariant.text],
          },
          badge.textStyle
        ]}>
          {badge.text}
        </Text>
      </View>
    );
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => {
        const inputClasses = [
          'input',
          `input-${variant}`,
          `input-${size}`,
          disabled && 'input-disabled',
          isFocused && 'input-focused',
          error && 'input-error',
          className,
        ].filter(Boolean).join(' ');

        return (
          <View style={styles.container}>
            {label && <Text style={styles.label}>{label}</Text>}
            <View style={[
              styles.inputContainer,
              isFocused && styles.focusedContainer,
              error && { borderColor: theme.colors.error },
              disabled && styles.disabled,
            ]}>
              {renderPrefix()}
              {renderIcon(leftIcon, 'left')}
              <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
                <TextInput
                  value={formatValue(value)}
                  onChangeText={(text) => handleChangeText(text, onChange)}
                  onBlur={() => {
                    onBlur();
                    setIsFocused(false);
                  }}
                  onFocus={() => {
                    setIsFocused(true);
                    onFocus?.();
                  }}
                  placeholder={placeholder}
                  placeholderTextColor={getVariantStyles().placeholderColor}
                  editable={!disabled && !isDateInput}
                  keyboardType={numeric ? (Platform.OS === 'ios' ? 'decimal-pad' : 'numeric') : 'default'}
                  style={[
                    styles.input,
                    leftIcon && { paddingLeft: InputConfig.addons.icon.size[size] + InputConfig.addons.icon.spacing * 2 },
                    rightIcon && { paddingRight: InputConfig.addons.icon.size[size] + InputConfig.addons.icon.spacing * 2 },
                    prefix && { paddingLeft: 45 },
                    button && { paddingRight: InputConfig.addons.button.height + InputConfig.addons.button.marginRight * 2 },
                    showIncrementButtons && { paddingHorizontal: InputConfig.addons.counter.width + InputConfig.addons.prefix.spacing * 2 },
                  ]}
                  selectTextOnFocus={selectTextOnFocus}
                  className={inputClasses}
                  {...Platform.select({
                    web: {
                      type: isDateInput ? 'text' : (numeric ? 'number' : 'text'),
                      min: scale === '0-100' ? 0 : scale === '0-10' ? 0 : scale === '0-1' ? 0 : undefined,
                      max: scale === '0-100' ? 100 : scale === '0-10' ? 10 : scale === '0-1' ? 1 : undefined,
                      step: scale === '0-1' ? 0.1 : 1,
                    },
                  })}
                  {...props}
                />
                {renderBadge()}
              </View>
              {renderIcon(rightIcon, 'right')}
              {renderButton()}
              {renderIncrementButtons()}
            </View>
            {!hideError && error?.message && (
              <Text style={styles.error}>{error.message}</Text>
            )}
          </View>
        );
      }}
    />
  );
}; 